import { useTranslation } from 'react-i18next';

import { IconManager } from '@speeki/icons';

import { useOrganisationLabel } from '@utils/hooks/useOrganisationLabel';

import {
    DisclosureHeader,
    DisclosureList,
    DisclosureListItem,
    DisclosureText,
    DisclosureTitle,
    DisclosureWrapper,
    PrivateNote,
} from './ReportDisclosureSummary.styled';

interface ReportDisclosureSummaryProps {
    /**
     * Whether the reporter still has the updates-email step ahead of them. Logged-in
     * reporters never see it, so promising them a private address they are never
     * asked for would be a lie.
     */
    hasUpdatesStep: boolean;
    /**
     * The form values as the reporter types them - this is the component's
     * only data source. The submission-time reactive var is written too late
     * for a summary that lives beside the fields it describes.
     */
    liveDetails: {
        email?: string | null;
        firstName?: string | null;
        lastName?: string | null;
    };
}

/**
 * Spells out what the organisation will see, alongside the fields where the
 * reporter decides it - so the disclosure is read at the moment of choice,
 * not several steps later.
 */
export const ReportDisclosureSummary = ({
    hasUpdatesStep,
    liveDetails,
}: ReportDisclosureSummaryProps) => {
    const { t } = useTranslation();
    const organisation = useOrganisationLabel();

    const email = liveDetails.email?.trim();
    const name = [liveDetails.firstName, liveDetails.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');

    const isAnonymous = !email && !name;

    return (
        <DisclosureWrapper data-testid="section_disclosure-summary">
            <DisclosureHeader>
                <IconManager
                    icon="SvgInfo"
                    size={16}
                    themeColors={{
                        default: {
                            'color-1': 'text.primary',
                        },
                    }}
                />
                <DisclosureTitle>
                    {t('REPORTER_DISCLOSURE_SUMMARY_TITLE').toUpperCase()}
                </DisclosureTitle>
            </DisclosureHeader>
            {isAnonymous ? (
                <DisclosureText>
                    {t('REPORTER_DISCLOSURE_SUMMARY_ANONYMOUS', {
                        organisation,
                    })}
                </DisclosureText>
            ) : (
                <>
                    <DisclosureText>
                        {t('REPORTER_DISCLOSURE_SUMMARY_SHARED', {
                            organisation,
                        })}
                    </DisclosureText>
                    <DisclosureList>
                        {/* These two rows are the reporter's identity, so they are
                         * excluded from capture like the fields they came from. */}
                        {!!name && (
                            <DisclosureListItem className="htmlToImageIgnore">
                                {t('REPORTER_DISCLOSURE_SUMMARY_NAME', {
                                    name,
                                })}
                            </DisclosureListItem>
                        )}
                        {!!email && (
                            <DisclosureListItem className="htmlToImageIgnore">
                                {t('REPORTER_DISCLOSURE_SUMMARY_EMAIL', {
                                    email,
                                })}
                            </DisclosureListItem>
                        )}
                        <DisclosureListItem>
                            {t('REPORTER_DISCLOSURE_SUMMARY_REPORT')}
                        </DisclosureListItem>
                    </DisclosureList>
                </>
            )}
            {hasUpdatesStep && (
                <PrivateNote>
                    {t('REPORTER_DISCLOSURE_SUMMARY_PRIVATE', { organisation })}
                </PrivateNote>
            )}
        </DisclosureWrapper>
    );
};
